import {
  Chip,
  Dialog,
  DialogTitle,
  IconButton,
  MenuItem,
  TableCell,
  TableRow,
} from '@mui/material';
import { format } from 'date-fns';
import { useState } from 'react';
import { WrappedPractitioner } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Practitioner';
import { WrappedPatient } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Patient';
import Iconify from 'src/components/Iconify';
import { TableMoreMenu } from 'src/components/table';
import Viewer from 'src/careflow/care-coordination/patient/assessment/viewer/Viewer';
import { getMimeIcon } from 'src/utils/getFileFormat';
import DocumentEditForm from './DocumentEditForm';
import { isEmpty } from 'lodash';
import { useFuegoApp } from 'src/careflow/fuego_app/useFuegoApp';

type Props = {
  row: any;
  patient?: WrappedPatient | null;
  patientId?: string;
  editAttachment: boolean;
  refreshDocumentReference: any;
  wrappedPractionersIndex?: {};
};

export default function DocumentRow({
  row,
  patient,
  patientId,
  refreshDocumentReference,
  editAttachment,
  wrappedPractionersIndex,
}: Props) {
  const [data, setData] = useState(null);
  const [openMenu, setOpenMenuActions] = useState<HTMLElement | null>(null);
  const [openModalEdit, setOpenModalEdit] = useState(false);
  const [openViewer, setOpenViewer] = useState(false);

  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>) => {
    setOpenMenuActions(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setOpenMenuActions(null);
  };

  const handleEdit = (row: any) => {
    handleCloseMenu();
    setData(row);
    setOpenModalEdit(true);
  };

  const handleCloseEdit = () => {
    setOpenModalEdit(false);
    refreshDocumentReference();
  };

  const handleOpenViewer = () => {
    setOpenViewer((prev) => !prev);
  };

  const handleCloseViewer = () => {
    setOpenViewer(false);
  };

  const { viewOnFuego, mergeOnFuego } = useFuegoApp();

  const handleViewOnFuego = (doc: any) => {
    handleCloseMenu();
    viewOnFuego({
      target: 'documentReference',
      primaryPatientId: patient?.id,
      currentPatientName: patient?.getFullName?.() || undefined,
      documentReference: doc,
    });
  };

  const handleMergeOnFuego = (doc: any) => {
    handleCloseMenu();
    mergeOnFuego({
      target: 'documentReference',
      primaryPatientId: patient?.id,
      currentPatientName: patient?.getFullName?.() || undefined,
      documentReference: doc,
    });
  };

  const title = !isEmpty(row?.content?.[0].attachment.title) 
    ? row?.content?.[0].attachment.title 
    : !isEmpty(row?.id)
    ? `file_${row?.id}`
    : 'file';

  return (
    <>
      <TableRow>
        <TableCell>
          <IconButton onClick={() => handleOpenViewer()}>
            <Iconify icon={'carbon:view'} sx={{ width: 28, height: 28 }} />
            <Iconify
              sx={{ width: 18, height: 18, mt: 1 }}
              icon={
                row?.content?.[0].attachment.contentType &&
                getMimeIcon(row.content?.[0].attachment.contentType).props.icon
              }
            />
          </IconButton>
        </TableCell>
        <TableCell style={{ cursor: 'pointer' }} sx={{ fontSize: `0.75rem` }}>
          {title}
        </TableCell>
        <TableCell>
          {row.author?.map((e: any, index: number) => {
            let authorDisplay = e.display ? e.display : e.text;

            if (
              !authorDisplay &&
              wrappedPractionersIndex &&
              e.reference &&
              e.reference.includes('Practitioner/')
            ) {
              const splitRef = e.reference.split('/');
              const practitionerId = splitRef[splitRef.length - 1];
              const wPractitioner = wrappedPractionersIndex[practitionerId] as WrappedPractitioner;

              if (wPractitioner) {
                authorDisplay = wPractitioner.getFullName();
              }
            }

            return (
              <Chip
                clickable={false}
                size="small"
                key={index}
                label={authorDisplay}
                sx={{ m: 0.2 }}
              />
            );
          })}
        </TableCell>
        <TableCell style={{ cursor: 'pointer' }} sx={{ fontSize: `0.75rem` }}>
          {row.custodian?.display}
        </TableCell>
        <TableCell style={{ cursor: 'pointer' }} sx={{ fontSize: `0.75rem` }}>
          {row.type?.coding?.[0].display}
        </TableCell>
        <TableCell>
          {row.category?.map((e: any, index: number) => (
            <Chip
              clickable={false}
              size="small"
              key={index}
              label={e.coding?.[0].display ? e.coding?.[0].display : e.text}
              sx={{ m: 0.2 }}
            />
          ))}
        </TableCell>
        <TableCell style={{ cursor: 'pointer' }} sx={{ fontSize: `0.75rem` }}>
          {row.date && format(new Date(row.date), 'MMM dd, yyyy hh:mm')}
        </TableCell>
        <TableCell align="right">
          <TableMoreMenu
            open={openMenu}
            onOpen={handleOpenMenu}
            onClose={handleCloseMenu}
            width={220}
            actions={[
              editAttachment && (
                <MenuItem key={'menu-i-edit-'} onClick={() => handleEdit(row)}>
                  <Iconify icon={'mdi:pencil'} />
                  Edit
                </MenuItem>
              ),
              <MenuItem key={'menu-i-view-fuego'} onClick={() => handleViewOnFuego(row)}>
                <Iconify icon={'eva:external-link-fill'} />
                View on Fuego App
              </MenuItem>,
              <MenuItem key={'menu-i-merge-fuego'} onClick={() => handleMergeOnFuego(row)}>
                <Iconify icon={'mdi:call-merge'} />
                Merge on Fuego App
              </MenuItem>,
            ].filter(Boolean)}
          />
        </TableCell>
      </TableRow>
      <DocumentEditForm 
        open={openModalEdit} 
        onClose={handleCloseEdit} 
        document={data} 
        wrappedPractionersIndex={wrappedPractionersIndex} 
      />
      <Dialog fullWidth maxWidth="md" open={openViewer} onClose={handleCloseViewer}>
        <DialogTitle>Preview Document</DialogTitle>
        <Viewer documentReference={row} typeResource="documentReference" patientId={patientId} />
      </Dialog>
    </>
  );
}
